import React, { useEffect, useState, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useRouter } from "next/router"
import { getListFirstLineItemsData } from "@/store/features/favoritesSlice"
import { selectGenericState } from "@/store/features/genericSlice"
import useWindowResize from "@/hooks/useWindowResize"
import { getConfig } from "@/constants/config"
import { getPresetUrl, onImageError } from "@/utils/"
import ListConfirmationModal from "@/components/ListConfirmationModal"
import { useTranslation } from "next-i18next"
import { getTranslations } from "@/components/MyFavorites/v1/MyList/localization"
import { geMonthNameDayandYear, sanitizeTextForAnalytics } from "@/utils/helper"
import { MY_LISTS_PAGE_PATH, PRODUCT_BEST_SELLERS } from "@/constants/routes"
import Image from "@/components/Shared/Image"
import wishIcon from "@/public/icons/wish.svg"
import filledWishIcon from "@/public/icons/wishlist-gray-filled.svg"
import { getLineItemsProductImage } from "@/components/MyFavorites/v1/MyList/helper"
import noProductsYet from "@/public/images/No-Products-Yet.png"
import {
  dataLayerDeleteListButton,
  dataLayerShopBestSellerButton,
  dataLayerViewListButton,
} from "@/components/MyFavorites/v1/MyList/analytics"

const ListTile = props => {
  const {
    list = {},
    presetConfigs = "",
    setSelectedList = () => {},
    general = {},
  } = props || {}
  const { t } = useTranslation("common")
  const staticText = getTranslations(t)
  const {
    globalConfig: { internationalization: { language = "en" } = {} } = {},
  } = useSelector(selectGenericState)
  const dispatch = useDispatch()
  const [width] = useWindowResize()
  const [listProductData, setListProductData] = useState({})
  const [images, setImages] = useState([])
  const listNameRef = useRef(null)
  const [listNameTruncated, setListNameTruncated] = useState(false)
  const [locale, setLocale] = useState("")
  const [emptyListModalOpen, setEmptyListModalOpen] = useState(false)
  const [emptyList, setEmptyList] = useState(false)
  const [deleteList, setDeleteList] = useState(false)
  const [selectedListId, setSelectedListId] = useState("")
  const router = useRouter()
  const listLineItemsLength = list?.lineItems?.length

  const getListInfo = (needsJSON = false) => {
    const listName = list?.name[locale]
    const listInfo = {
      description:
        sanitizeTextForAnalytics(
          list?.description ? list?.description[locale] : ""
        ) || "n/a",
      listID: list?.id || "n/a",
      listName: sanitizeTextForAnalytics(listName) || "n/a",
      lineItemsCount: list?.lineItems?.length || 0,
      listUpdatedProject: `${geMonthNameDayandYear(
        list?.lastModifiedAt,
        locale
      )}`,
      listLocation: (list?.custom?.fields?.project ?? "") || "n/a",
    }
    if (needsJSON) {
      return listInfo.listInfo
    }
    return JSON.stringify(listInfo)
  }

  const listInfo = JSON.parse(getListInfo(false))

  const handleListView = (count, isFavImg = false, list) => {
    const myListTitle = staticText?.myListPageTitle
    const viewListCTATitle = staticText?.viewList
    const listName = list?.name[locale]
    const idView = `?listId=${selectedListId}`
    dataLayerViewListButton(
      myListTitle,
      viewListCTATitle,
      listName,
      listInfo,
      idView
    )
    if (!count) {
      setEmptyList(true)
      setEmptyListModalOpen(true)
    } else if (!isFavImg) {
      setSelectedList(selectedListId)
      router.push({
        pathname: `/${language}${MY_LISTS_PAGE_PATH}`,
        query: { listId: selectedListId },
      })
    }
  }

  const handleShopOurBestSellars = (count, list) => {
    if (!count) {
      const myListTitle = staticText?.myListPageTitle
      const shopBestSellerCTATitle = staticText?.shoupOurBestSellerListBtn
      dataLayerShopBestSellerButton(
        myListTitle,
        shopBestSellerCTATitle,
        list?.name[locale],
        listInfo
      )
      router.push(`/${language}${PRODUCT_BEST_SELLERS}`).then(() => {
        router.reload()
      })
    } else {
      handleListView(count, false, list)
    }
  }
  const closeConformationModal = () => {
    setEmptyListModalOpen(false)
    setDeleteList(false)
    setEmptyList(false)
  }
  const handleListDelete = list => {
    const myListTitle = staticText?.myListPageTitle
    const deleteListTitle = staticText?.deleteList
    dataLayerDeleteListButton(
      myListTitle,
      deleteListTitle,
      list?.name[locale],
      listInfo
    )
    setDeleteList(true)
    setEmptyListModalOpen(true)
  }

  const getListProductData = () => {
    dispatch(getListFirstLineItemsData(list, false))
      .unwrap()
      .then(response => {
        setListProductData(response)
        const images = getLineItemsProductImage(response[0])
        setImages(images)
      })
      .catch(error => {
        console.log(error)
      })
  }

  useEffect(() => {
    if (listLineItemsLength) {
      getListProductData()
    } else {
      setImages(["MissingImage"])
    }
    setSelectedListId(listLineItemsLength ? list?.id : "")
  }, [list, listLineItemsLength])

  useEffect(() => {
    getConfig().then(config => {
      const { internationalization: { locale = "en-US" } = {} } = config
      const localeVAlue = locale === "fr-CA" ? "en-CA" : locale
      setLocale(localeVAlue)
    })
  }, [])

  useEffect(() => {
    const listNameEl = listNameRef.current
    if (listNameEl) {
      setListNameTruncated(listNameEl?.scrollWidth > listNameEl?.clientWidth)
    }
  }, [list?.name[locale]])

  const noProcutsImageComponent = (id, i) => {
    return (
      <Image
        key={id + "_IMG_" + i}
        id={id + "_IMG_" + i}
        src={noProductsYet}
        role="presentation"
        alt="Product image not available"
        onClick={() => {
          handleListView(listLineItemsLength)
        }}
      />
    )
  }

  return (
    <div className="userListTile">
      <div
        className="listImage"
        onClick={() => {
          handleListView(listLineItemsLength)
        }}
      >
        <div className="product-image">
          {listLineItemsLength
            ? images.map((id, i) => {
                const imgUrl = getPresetUrl(
                  width,
                  0,
                  general.swatchUrl,
                  id,
                  JSON.parse(presetConfigs)
                )
                return (
                  <img
                    key={id + "_IMG_" + i}
                    id={id + "_IMG_" + i}
                    role="presentation"
                    src={imgUrl}
                    alt={listProductData.metaTitle_s}
                    imgsrc={imgUrl}
                    imgalt={listProductData.metaTitle_s}
                    onError={e => onImageError(e, imgUrl)}
                  />
                )
              })
            : noProcutsImageComponent("missingImage", 0)}
        </div>
      </div>
      <div className="listDetailsContainer">
        <div className="listDetails">
          <div
            className="listName"
            onClick={() => {
              handleListView(listLineItemsLength)
            }}
            ref={listNameRef}
          >
            {list?.name[locale]}
            {listNameTruncated ? (
              <span className="listNameToolTip">{list?.name[locale]}</span>
            ) : null}
          </div>
          <div className="listUpdatedProject">{`${t(
            "kf.myList.page.updated"
          )}: ${geMonthNameDayandYear(list?.lastModifiedAt, locale)}`}</div>
          <div className="listUpdatedProject">
            {list?.custom?.fields?.project ?? ""}
          </div>
        </div>
        <div className="countAndFavImage">
          <div
            className="favImage"
            onClick={() => handleListView(listLineItemsLength, true)}
          >
            <Image
              src={listLineItemsLength ? filledWishIcon?.src : wishIcon?.src}
              role="presentation"
              alt="close"
              width="30"
              height="30"
            />
          </div>
          <div className="lineItemsCount">{list?.lineItems?.length}</div>
        </div>
      </div>
      <div className="listDiscription">
        {list?.description ? list?.description[locale] : ""}
      </div>
      <div
        className="viewListBtn"
        onClick={() => {
          handleShopOurBestSellars(listLineItemsLength, list)
        }}
      >
        <div className="viewListText">
          {listLineItemsLength
            ? staticText?.viewList
            : staticText?.shoupOurBestSellerListBtn}
        </div>
      </div>
      <div className="deleteList">
        <div
          className="deleteOuterContainer"
          onClick={() => {
            handleListDelete(list)
          }}
        >
          <div className="deleteListText">{staticText?.deleteList}</div>
        </div>
      </div>
      {emptyListModalOpen ? (
        <ListConfirmationModal
          list={list}
          openConforMationModal={emptyListModalOpen}
          noLineItems={emptyList}
          deleteList={deleteList}
          closeConformationModal={closeConformationModal}
        />
      ) : null}
      <div className="tileDevider"></div>
    </div>
  )
}
export default ListTile
